import { useSearchParams } from 'react-router-dom';
import ApiClientManager from 'core/apiClient';

export interface CurrentContext {
  tenant: string | undefined;
  realmId: string | undefined;
  /** Whether context was extracted from URL params (true) or auth defaults (false) */
  isFromUrl: boolean;
}

/**
 * Single source of truth for current tenant/realm context.
 *
 * Priority:
 * 1. URL query params (tenant, realmId) - for shareable links and cross-tenant navigation
 * 2. Auth defaults from ApiClientManager - the tenant/realm user logged into
 *
 * @returns {CurrentContext} Current context with source indicator
 */
export const useCurrentContext = (): CurrentContext => {
  const [searchParams] = useSearchParams();

  const urlTenant = searchParams.get('tenant');
  const urlRealmId = searchParams.get('realmId');

  // Auth defaults (set once at login, stored in localStorage)
  const authDefaultTenant = ApiClientManager.getAuthDefaultTenant()?.tenantName;
  const authDefaultRealmId = ApiClientManager.getAuthDefaultRealmId();

  const tenant = urlTenant ?? authDefaultTenant;
  const realmId = urlRealmId ?? authDefaultRealmId;
  const isFromUrl = !!(urlTenant || urlRealmId);

  return { tenant, realmId, isFromUrl };
};
